import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UtensilsCrossed, Wine, Beer, GlassWater, Coffee, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  foodCollage: "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/1u9lx43t_94CC5DD0-F753-43B7-8CA5-E83BDC9C5993.png",
  fishChips: "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/h1ax4wes_b19796f0-2537-4e93-ab55-688324b8ff24.jpeg", // Fish & chips
  chickenFries: "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/dfm8z314_5f97f949-0d14-4f6d-9ad7-4afbb838aade.jpeg", // Chicken with cream sauce
  interior: "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/e6tut9t0_a199382b-d752-4635-8660-55d9cd15bcaf.jpeg", // Interior
};

// Drinks category images
const DRINK_IMAGES = {
  beer: "https://images.pexels.com/photos/5864291/pexels-photo-5864291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  wine: "https://images.unsplash.com/photo-1692641218321-2acead168a14?crop=entropy&cs=srgb&fm=jpg&w=800",
  cocktails: "https://images.pexels.com/photos/10325196/pexels-photo-10325196.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  whiskey: "https://images.pexels.com/photos/29392061/pexels-photo-29392061.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  vodka: "https://images.pexels.com/photos/34837794/pexels-photo-34837794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  gin: "https://images.pexels.com/photos/13700907/pexels-photo-13700907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  rum: "https://images.unsplash.com/photo-1651410634315-56a535912396?crop=entropy&cs=srgb&fm=jpg&w=800",
  coffee: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&auto=format&fit=crop",
  softdrinks: "https://images.unsplash.com/photo-1625772299848-391b6a87d7b3?w=800&auto=format&fit=crop",
};

// Complete drinks menu from PDF
const DRINKS_MENU = {
  beer: {
    title: "Beer",
    image: DRINK_IMAGES.beer,
    items: [
      { name: "Guinness (Draught)", price: "5.60 / 9.80" },
      { name: "Kilkenny (Draught)", price: "5.60 / 9.80" },
      { name: "House Beer (Draught)", price: "4.90 / 9.00" },
      { name: "Gurten Bier (Draught)", price: "5.50 / 9.00" },
      { name: "Astra (Draught)", price: "5.50 / 9.00" },
      { name: "Brooklyn Lager (Draught)", price: "6.50 / 9.80" },
      { name: "Super Bock (Draught)", price: "5.50 / 9.80" },
      { name: "Valaisanne Lager (Draught)", price: "5.50 / 9.00" },
      { name: "Kingfisher / Cobra", price: "7.00" },
      { name: "Simmentaler Märzen", price: "7.00" },
      { name: "Super Bock (G-Frei)", price: "7.00" },
      { name: "Schneider Weisse", price: "7.90" },
      { name: "Corona", price: "7.00" },
      { name: "Poretti", price: "7.00" },
      { name: "Feldschlösschen", price: "7.90" },
      { name: "Ginger Beer", price: "7.00" },
      { name: "Feldschlösschen (A-Frei)", price: "7.00" },
    ]
  },
  wine: {
    title: "Wine Selection",
    subtitle: "1 DL",
    image: DRINK_IMAGES.wine,
    items: [
      { name: "White - Chardonnay", price: "7.90" },
      { name: "Red - Primitivo", price: "7.90" },
      { name: "Rosé - Oeil de Perdrix", price: "7.90" },
    ]
  },
  cocktails: {
    title: "Cocktails & Spritz",
    image: DRINK_IMAGES.cocktails,
    items: [
      { name: "Lillet Wildberry", price: "11.90" },
      { name: "Hugo", price: "11.90" },
      { name: "Aperol Spritz", price: "11.90" },
      { name: "Campari Spritz", price: "11.90" },
      { name: "Limoncello Spritz", price: "11.90" },
      { name: "Mojito", price: "11.90" },
      { name: "Martini Spritz", price: "11.90" },
      { name: "Espresso Martini", price: "11.90" },
    ]
  },
  longdrinks: {
    title: "Long Drinks",
    image: DRINK_IMAGES.gin,
    items: [
      { name: "Cuba Libre", price: "12.90" },
      { name: "Gin Tonic", price: "12.90" },
      { name: "Campari Soda", price: "12.90" },
      { name: "Vodka Red Bull", price: "12.90" },
    ]
  },
  whiskey: {
    title: "Whiskey",
    image: DRINK_IMAGES.whiskey,
    items: [
      { name: "Jameson", price: "9.00" },
      { name: "Tullamore D.E.W.", price: "10.00" },
      { name: "Bushmills", price: "11.00" },
      { name: "Redbreast 12", price: "15.00" },
      { name: "Jack Daniel's", price: "9.00" },
      { name: "Johnny Walker Black", price: "12.00" },
    ]
  },
  spirits: {
    title: "Vodka, Gin & Rum",
    image: DRINK_IMAGES.vodka,
    items: [
      { name: "Grey Goose Vodka", price: "12.00" },
      { name: "Absolut Vodka", price: "9.00" },
      { name: "Bombay Sapphire Gin", price: "10.00" },
      { name: "Hendrick's Gin", price: "12.00" },
      { name: "Bacardi Rum", price: "9.00" },
      { name: "Captain Morgan", price: "9.00" },
      { name: "Havana Club", price: "10.00" },
    ]
  },
  softdrinks: {
    title: "Soft Drinks",
    image: DRINK_IMAGES.softdrinks,
    items: [
      { name: "Coca Cola / Zero", price: "5.00" },
      { name: "Pepsi / Max", price: "5.00" },
      { name: "Rivella Rot", price: "5.00" },
      { name: "Fanta / Sprite", price: "5.00" },
      { name: "Apfelschorle", price: "5.00" },
      { name: "Ice Tea", price: "5.00" },
      { name: "Arkina Still Water 35cl", price: "5.00" },
      { name: "Ginger Beer", price: "7.00" },
    ]
  },
  hotdrinks: {
    title: "Hot Drinks",
    image: DRINK_IMAGES.coffee,
    items: [
      { name: "Coffee / Espresso / Ristretto", price: "5.00" },
      { name: "Double Espresso", price: "6.00" },
      { name: "Cappuccino / Milk Coffee", price: "6.50" },
      { name: "Latte Macchiato", price: "6.50" },
      { name: "Hot Chocolate", price: "6.50" },
      { name: "Bailey's Coffee", price: "10.00" },
      { name: "Tea Selection", price: "5.00" },
    ]
  },
};

// Drinks Category Card Component
const DrinksCategoryCard = ({ category, data, onClick }) => (
  <div 
    data-testid={`drinks-category-${category}`}
    onClick={onClick}
    className="relative group cursor-pointer overflow-hidden rounded-sm aspect-[4/3] bg-[#0b1412]"
  >
    <img 
      src={data.image} 
      alt={data.title}
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050a09] via-[#050a09]/60 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#E8E6D9] group-hover:text-[#D4AF37] transition-colors">
        {data.title}
      </h3>
      {data.subtitle && (
        <p className="text-[#A3A3A3] text-sm font-['Manrope']">{data.subtitle}</p>
      )}
      <p className="text-[#D4AF37] text-sm font-['Manrope'] mt-1">
        {data.items.length} items
      </p>
    </div>
    <div className="absolute top-3 right-3 w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
      <Sparkles size={16} className="text-[#D4AF37]" />
    </div>
  </div>
);

// Drinks Detail Modal/Section
const DrinksDetailSection = ({ category, data, onClose }) => (
  <div className="bg-[#050a09] border border-white/10 rounded-sm p-6 mb-8">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-sm overflow-hidden">
          <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
        </div>
        <div>
          <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#D4AF37]">
            {data.title}
          </h3>
          {data.subtitle && (
            <p className="text-[#A3A3A3] text-sm font-['Manrope']">{data.subtitle}</p>
          )}
        </div>
      </div>
      <button 
        onClick={onClose}
        className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
      >
        ← Back to all
      </button>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-8">
      {data.items.map((item) => (
        <div 
          key={`${data.title}-${item.name}-${item.price}`}
          className="flex justify-between items-center border-b border-white/5 py-3 hover:border-[#D4AF37]/30 transition-colors"
        >
          <span className="text-[#E8E6D9] font-['Manrope']">{item.name}</span>
          <span className="text-[#D4AF37] font-['Manrope'] font-semibold whitespace-nowrap ml-4">
            CHF {item.price}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const categoryIcons = {
  loaded_fries: <UtensilsCrossed size={20} />,
  pub_snacks: <UtensilsCrossed size={20} />,
  classics: <UtensilsCrossed size={20} />,
  sharers: <UtensilsCrossed size={20} />,
  burgers: <UtensilsCrossed size={20} />,
  beers: <Beer size={20} />,
  whiskey: <GlassWater size={20} />,
  cocktails: <Wine size={20} />,
  wines: <Wine size={20} />,
};

const categoryLabels = {
  loaded_fries: 'Loaded Fries',
  pub_snacks: 'Pub Snacks',
  classics: 'Classics',
  sharers: 'Sharers',
  burgers: 'Burgers',
  beers: 'Beers on Tap',
  whiskey: 'Irish Whiskey',
  cocktails: 'Cocktails',
  wines: 'Wines',
};

const MenuItem = ({ item }) => (
  <div 
    data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
    className="flex justify-between items-start border-b border-white/5 pb-4 mb-4 hover:border-[#D4AF37]/30 transition-colors menu-item group"
  >
    <div className="flex-1 pr-4">
      <h4 className="font-['Manrope'] font-semibold text-[#E8E6D9] group-hover:text-[#D4AF37] transition-colors">
        {item.name}
      </h4>
      <p className="text-[#A3A3A3] text-sm mt-1 font-['Manrope']">{item.description}</p>
      {item.note && (
        <p className="text-[#D4AF37]/70 text-xs mt-2 font-['Manrope'] italic">{item.note}</p>
      )}
    </div>
    <div className="price-tag text-lg whitespace-nowrap">
      CHF {item.price.toFixed(2)}
    </div>
  </div>
);

const MenuCategory = ({ title, items, icon }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <span className="text-[#D4AF37]">{icon}</span>
      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#E8E6D9]">
        {title}
      </h3>
    </div>
    <div>
      {items.map((item) => (
        <MenuItem key={`${title}-${item.name}-${item.price}`} item={item} />
      ))}
    </div>
  </div>
);

const MenuPage = () => {
  const [menu, setMenu] = useState(null);
  const [activeTab, setActiveTab] = useState('drinks');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDrinkCategory, setSelectedDrinkCategory] = useState(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        setMenu(response.data);
      } catch {
        // Fallback menu data
        setMenu({
          food: {
            loaded_fries: [
              { name: "Paprika Fries", price: 8.90, description: "Crispy fries with paprika powder" },
              { name: "Jalapeños Fries", price: 9.90, description: "Jalapeños & coriander mayo" },
              { name: "Cheesy Bacon Fries", price: 11.90, description: "Cheddar cheese sauce, bacon & spring onion" }
            ],
            pub_snacks: [
              { name: "Chicken Wings", price: 12.90, description: "BBQ / Spicy / Garlic Mayo" },
              { name: "Chicken Nuggets", price: 12.90, description: "Crispy nuggets with dipping sauce" },
              { name: "Onion Rings", price: 8.90, description: "Served with spicy mayo" }
            ],
            classics: [
              { name: "Club Sandwich", price: 22.90, description: "Chicken, egg, bacon, lettuce, tomato & cocktail sauce" },
              { name: "Fish & Chips", price: 25.90, description: "Crispy battered fish, fries & tartar sauce" }
            ],
            sharers: [
              { name: "Loaded Nachos", price: 12.90, description: "Cheese, jalapeños, salsa & guacamole" }
            ],
            burgers: [
              { name: "Veg Burger", price: 18.90, description: "Veg patty, cheddar, lettuce, tomato & house sauce" },
              { name: "Chicken Burger", price: 20.90, description: "Crispy chicken, spicy mayo, lettuce & pickles" },
              { name: "Beef Burger (180g)", price: 22.90, description: "Cheddar, tomato, onions & pub sauce", note: "All burgers are served with fries" }
            ]
          },
          drinks: {
            beers: [
              { name: "Guinness Draught", price: 7.50, description: "The iconic Irish stout" },
              { name: "Kilkenny", price: 7.00, description: "Irish cream ale" },
              { name: "Local Lager", price: 6.50, description: "Fresh Swiss lager" },
              { name: "Craft IPA", price: 8.00, description: "Hoppy India Pale Ale" }
            ],
            whiskey: [
              { name: "Jameson", price: 9.00, description: "Classic Irish whiskey" },
              { name: "Tullamore D.E.W.", price: 10.00, description: "Triple distilled smooth" },
              { name: "Bushmills", price: 11.00, description: "Single malt Irish" },
              { name: "Redbreast 12", price: 15.00, description: "Premium pot still" }
            ],
            cocktails: [
              { name: "Irish Coffee", price: 12.00, description: "Coffee, Irish whiskey, cream" },
              { name: "Guinness Cocktail", price: 14.00, description: "Guinness reduction, vanilla, espresso" },
              { name: "Dublin Mule", price: 13.00, description: "Irish whiskey, ginger beer, lime" }
            ],
            wines: [
              { name: "House Red", price: 7.00, description: "Glass of selected red wine" },
              { name: "House White", price: 7.00, description: "Glass of selected white wine" },
              { name: "Prosecco", price: 8.00, description: "Italian sparkling" }
            ]
          }
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenu();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 bg-[#050a09] flex items-center justify-center">
        <div className="text-[#D4AF37] font-['Manrope']">Loading menu...</div>
      </div>
    );
  }

  return (
    <div data-testid="menu-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#050a09]">
        <div className="absolute inset-0 opacity-20">
          <img
            src={ASSETS.burger}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050a09] via-[#050a09]/80 to-[#050a09]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
            Our Selection
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#E8E6D9] mb-4">
            Food & <span className="text-[#D4AF37]">Drinks</span>
          </h1>
          <p className="text-[#A3A3A3] font-['Manrope'] text-lg">
            Authentic pub fare and premium spirits
          </p>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-16 md:py-24 bg-[#0b1412]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList 
              data-testid="menu-tabs"
              className="w-full justify-center bg-transparent border-b border-white/10 rounded-none h-auto pb-0 mb-12"
            >
              <TabsTrigger
                value="food"
                data-testid="tab-food"
                className="text-lg font-['Playfair_Display'] px-8 py-4 data-[state=active]:text-[#D4AF37] data-[state=active]:border-b-2 data-[state=active]:border-[#D4AF37] data-[state=active]:bg-transparent rounded-none text-[#A3A3A3] hover:text-[#E8E6D9] transition-colors"
              >
                <UtensilsCrossed size={20} className="mr-2" />
                Food
              </TabsTrigger>
              <TabsTrigger
                value="drinks"
                data-testid="tab-drinks"
                className="text-lg font-['Playfair_Display'] px-8 py-4 data-[state=active]:text-[#D4AF37] data-[state=active]:border-b-2 data-[state=active]:border-[#D4AF37] data-[state=active]:bg-transparent rounded-none text-[#A3A3A3] hover:text-[#E8E6D9] transition-colors"
              >
                <Wine size={20} className="mr-2" />
                Drinks
              </TabsTrigger>
            </TabsList>

            {/* Food Tab */}
            <TabsContent value="food" className="mt-0">
              <div className="grid md:grid-cols-2 gap-x-16">
                <div>
                  {/* Featured Image - Fish & Chips */}
                  <div className="mb-8 rounded-sm overflow-hidden">
                    <img 
                      src={ASSETS.fishChips} 
                      alt="Fish & Chips"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  
                  {menu?.food?.loaded_fries && (
                    <MenuCategory 
                      title={categoryLabels.loaded_fries}
                      items={menu.food.loaded_fries}
                      icon={categoryIcons.loaded_fries}
                    />
                  )}
                  {menu?.food?.pub_snacks && (
                    <MenuCategory 
                      title={categoryLabels.pub_snacks}
                      items={menu.food.pub_snacks}
                      icon={categoryIcons.pub_snacks}
                    />
                  )}
                  {menu?.food?.sharers && (
                    <MenuCategory 
                      title={categoryLabels.sharers}
                      items={menu.food.sharers}
                      icon={categoryIcons.sharers}
                    />
                  )}
                </div>
                <div>
                  {/* Featured Image - Chicken with Fries */}
                  <div className="mb-8 rounded-sm overflow-hidden">
                    <img 
                      src={ASSETS.chickenFries} 
                      alt="Pub Classics"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  
                  {menu?.food?.classics && (
                    <MenuCategory 
                      title={categoryLabels.classics}
                      items={menu.food.classics}
                      icon={categoryIcons.classics}
                    />
                  )}
                  {menu?.food?.burgers && (
                    <MenuCategory 
                      title={categoryLabels.burgers}
                      items={menu.food.burgers}
                      icon={categoryIcons.burgers}
                    />
                  )}
                </div>
              </div>

              {/* Note */}
              <div className="mt-8 text-center">
                <p className="text-[#525252] text-sm font-['Manrope']">
                  All prices in CHF. Please inform us of any allergies.
                </p>
              </div>
            </TabsContent>

            {/* Drinks Tab */}
            <TabsContent value="drinks" className="mt-0">
              {selectedDrinkCategory ? (
                <DrinksDetailSection 
                  category={selectedDrinkCategory}
                  data={DRINKS_MENU[selectedDrinkCategory]}
                  onClose={() => setSelectedDrinkCategory(null)}
                />
              ) : (
                <>
                  <p className="text-center text-[#A3A3A3] font-['Manrope'] mb-8">
                    Click on a category to view all items
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(DRINKS_MENU).map(([key, data]) => (
                      <DrinksCategoryCard 
                        key={key}
                        category={key}
                        data={data}
                        onClick={() => setSelectedDrinkCategory(key)}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Note */}
              <div className="mt-8 text-center">
                <p className="text-[#525252] text-sm font-['Manrope']">
                  All prices in CHF. Please drink responsibly.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#050a09]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#E8E6D9] mb-4">
            Ready to <span className="text-[#D4AF37]">Dine?</span>
          </h2>
          <p className="text-[#A3A3A3] font-['Manrope'] mb-8">
            Reserve your table and enjoy an authentic Irish pub experience.
          </p>
          <a
            href="/reservations"
            data-testid="menu-reserve-btn"
            className="inline-block bg-[#D4AF37] text-[#050a09] px-8 py-4 rounded-sm font-bold text-sm tracking-wider uppercase hover:bg-[#E5C568] transition-all duration-300 btn-glow"
          >
            Reserve a Table
          </a>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
